import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getHotTitles, paginate, searchTitles, SORT_OPTIONS } from "@/lib/catalog";
import type { SortKey } from "@/types/film";
import TitleGrid from "@/components/feature/TitleGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";

const TYPE_TABS: { key: "all" | "movie" | "tv"; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const typeParam = searchParams.get("type");
  const type: "all" | "movie" | "tv" =
    typeParam === "movie" || typeParam === "tv" ? typeParam : "all";
  const sort = (searchParams.get("sort") as SortKey) || "popularity";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const [input, setInput] = useState(q);

  useEffect(() => {
    setInput(q);
  }, [q]);

  usePageMeta(
    q ? `搜索“${q}” - 欧美精品在线观看` : "搜索影视 - 欧美精品在线观看",
    q
      ? `在欧美精品在线观看站内搜索“${q}”的欧美电影、电视剧结果，支持按类型筛选与排序。`
      : "搜索欧美电影与电视剧资源，输入关键词快速找到想看的影片。",
    "/search"
  );

  const results = useMemo(() => searchTitles(q, { type, sort }), [q, type, sort]);

  const { items, totalPages, page: safePage, total } = paginate(results, page);
  const hotTitles = useMemo(() => getHotTitles(6), []);

  const submit = () => {
    const next = new URLSearchParams(searchParams);
    const value = input.trim();
    if (value) next.set("q", value);
    else next.delete("q");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const clearSearch = () => {
    setInput("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
      <header>
        <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
          {q ? "搜索结果" : "站内搜索"}
        </h1>
        <p className="mt-2 text-sm text-foreground-400">
          {q ? (
            <>
              当前关键词：<span className="text-accent-400">“{q}”</span>
            </>
          ) : (
            "输入影片名称、原名、类型或演员，快速查找影视资源"
          )}
        </p>
      </header>

      <form
        className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3 py-2 focus-within:border-primary-500">
          <i className="ri-search-line text-base text-foreground-400" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索影片、原名、类型、演员"
            aria-label="搜索关键词"
            className="flex-1 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 outline-none"
          />
          {input ? (
            <button
              type="button"
              onClick={() => setInput("")}
              aria-label="清空关键词"
              className="text-foreground-500 transition-colors hover:text-foreground-200 cursor-pointer"
            >
              <i className="ri-close-circle-line text-base" />
            </button>
          ) : null}
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-foreground-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
        >
          搜索
        </button>
      </form>

      {q ? (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-800 bg-background-900 px-4 py-3">
            <div className="flex items-center gap-2">
              {TYPE_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => updateParam("type", tab.key === "all" ? null : tab.key)}
                  className={`rounded-full px-3 py-1.5 text-xs transition-colors whitespace-nowrap cursor-pointer md:text-sm ${
                    type === tab.key
                      ? "bg-primary-500 font-medium text-foreground-50"
                      : "border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-foreground-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              <span className="ml-1 text-sm text-foreground-400">
                共 <span className="font-medium text-foreground-100">{total}</span> 条结果
              </span>
            </div>

            <select
              value={sort}
              onChange={(e) => updateParam("sort", e.target.value)}
              aria-label="排序方式"
              className="rounded-md border border-background-700 bg-background-950 px-3 py-1.5 text-sm text-foreground-100 outline-none transition-colors focus:border-primary-500 cursor-pointer"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id} className="bg-background-900">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {items.length > 0 ? (
            <>
              <div className="mt-6">
                <TitleGrid items={items} className="lg:grid-cols-4" />
              </div>
              <Pagination
                page={safePage}
                totalPages={totalPages}
                onChange={(p) => updateParam("page", String(p))}
              />
            </>
          ) : (
            <div className="mt-6 space-y-8">
              <EmptyState
                title="未找到相关影视内容"
                description={`没有找到与“${q}”匹配的影片，换个关键词试试，或浏览下面的热门内容。`}
                actionLabel="清空关键词"
                onAction={clearSearch}
              />
              <div>
                <div className="mb-4 flex items-center gap-3">
                  <span className="block h-5 w-1 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-lg font-bold text-foreground-50">热门推荐</h2>
                </div>
                <TitleGrid items={hotTitles} className="lg:grid-cols-3 xl:grid-cols-6" />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8">
          <div className="mb-4 flex items-center gap-3">
            <span className="block h-5 w-1 rounded-full bg-accent-500" />
            <h2 className="font-heading text-lg font-bold text-foreground-50">热门搜索推荐</h2>
          </div>
          <TitleGrid items={hotTitles} className="lg:grid-cols-3 xl:grid-cols-6" />
        </div>
      )}
    </div>
  );
}